import { Stack, Typography } from '@mui/material';
import {
  ConditionDatesForm,
  CreateSalesPriceConditionSchema,
  SalesPriceConditionPrioritization,
} from '@retail/calculus/components';
import { useController, useFormContext } from 'react-hook-form';
import { PriceSuggestionPrice } from '@retail/price-suggestion/types';
import { PriceSuggestionType } from '@retail/products/types';
import { useCallback } from 'react';
import { usePriceSuggestionTFunction } from '@retail/price-suggestion/i18n';
import { HelperText } from '@shared/components';
import { useFetchPriceSuggestionItemPrices } from '@retail/price-suggestion/data-access';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { MarkupBinding } from '@retail/calculus/types';

interface Props {
  mgSupplierId: number;
  mgItemNumber: number;
  includeMarkup: boolean;
  markupCondition?: MarkupBinding;
  isLoadingFetchPriceSuggestions: boolean;
}

export function CreateItemLevelSalesPriceConditionForm({
  mgSupplierId,
  mgItemNumber,
  includeMarkup,
  markupCondition,
  isLoadingFetchPriceSuggestions,
}: Props) {
  const t = usePriceSuggestionTFunction();
  const { assortmentId } = useSelectedOrgUnit();

  const { control } = useFormContext<CreateSalesPriceConditionSchema>();

  const priceSuggestionController = useController({ name: 'priceSourcePriority', control });
  const markupCoverageDegreeController = useController({ name: 'markupCoverageDegree', control });

  const { data: itemLevelPriceSuggestionDTO = [] } = useFetchPriceSuggestionItemPrices(
    { assortmentId, mgSupplierId, mgItemNumber },
    { suspense: false }
  );
  const itemLevelPriceSuggestion = itemLevelPriceSuggestionDTO?.map(PriceSuggestionPrice.fromDto);

  const updatePriceSuggestionPrioritization = useCallback(
    (value: PriceSuggestionType[]) => {
      priceSuggestionController.field.onChange(value);
    },
    [priceSuggestionController.field]
  );

  const getPriceTypeValue = useCallback(
    (priceSuggestionType: PriceSuggestionType) => {
      const priceSuggestion = itemLevelPriceSuggestion.find(
        (priceSuggestion) => priceSuggestion.priceSuggestionType === priceSuggestionType
      );
      return t('priceSuggestion.pricePerUnit', {
        price: priceSuggestion?.price,
        unit: priceSuggestion?.unit.description,
      });
    },
    [itemLevelPriceSuggestion, t]
  );

  const setMarkupSalesCoverageDegree = useCallback(
    (value: string | null) => {
      markupCoverageDegreeController.field.onChange(value);
    },
    [markupCoverageDegreeController.field]
  );

  return (
    <form>
      <Stack gap={3} pb={1}>
        {itemLevelPriceSuggestion.length === 0 && !includeMarkup ? (
          <Typography px={3}>{t('priceSuggestion.noPriceSuggestion')}</Typography>
        ) : (
          <>
            <SalesPriceConditionPrioritization
              priceSuggestionPriority={priceSuggestionController.field.value}
              availablePriceSuggestions={itemLevelPriceSuggestion.map(
                (type) => type.priceSuggestionType
              )}
              includeMarkup={includeMarkup}
              markupCondition={markupCondition}
              currentMarkup={markupCoverageDegreeController.field.value}
              updatePrizeSuggestionPrioritazion={updatePriceSuggestionPrioritization}
              getPriceTypeValue={getPriceTypeValue}
              setMarkupSalesCoverageDegree={setMarkupSalesCoverageDegree}
              isLoadingFetchPriceSuggestions={isLoadingFetchPriceSuggestions}
            />

            {priceSuggestionController.fieldState.error && (
              <Stack px={3}>
                <HelperText id={'pricesuggestion'} status={'error'}>
                  {t('priceSuggestion.error.selectPriceSuggestion')}
                </HelperText>
              </Stack>
            )}

            <Stack px={2}>
              <ConditionDatesForm excludeToDate labelAlignment="row" />
            </Stack>
          </>
        )}
      </Stack>
    </form>
  );
}
